import { useCallback, useEffect, useRef, useState } from 'react';
import { Chip, Stack, styled, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { DocumentReferencesView } from 'api';
import { NO_REFERENCED_DOCUMENTS } from 'common/constants/documents';
import { PendingReference, ReferencedDocument } from '../documents';
import { ReferencedDocumentListProps } from './ReferencedDocumentsList.props';
import { DocumentReference } from '../../../../context';
import { FileToSave } from '../../../../services/hooks/useDocuments';

const PREFIX = 'ReferencedDocumentsList';

const classes = {
  listItem: `${PREFIX}-listItem`,
  noReferencedDocuments: `${PREFIX}-noReferencedDocuments`,
  fileName: `${PREFIX}-fileName`,
  scrollIndicator: `${PREFIX}-scrollIndicator`,
};

const StyledChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  bottom: '8px',
  left: '20rem',
  zIndex: 1,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: '0.75rem',
  height: '1rem',
  width: '2rem',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const ReferencedDocumentsList = ({
  documents,
  selectedMeasurementDate,
  currentPage,
  isDisabled,
  areDocumentsActionsDisabled,
  onClick,
  filesToSave,
  documentSourceProps,
}: ReferencedDocumentListProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [documentsBelow, setDocumentsBelow] = useState(0);

  const calculateDocumentsBelow = useCallback(() => {
    if (!scrollContainerRef.current || documents.length === 0) {
      setDocumentsBelow(0);
      return;
    }

    const container = scrollContainerRef.current;
    const containerHeight = container.clientHeight;
    const { scrollTop } = container;
    const { scrollHeight } = container;

    // If content doesn't overflow, no documents are below
    if (scrollHeight <= containerHeight) {
      setDocumentsBelow(0);
      return;
    }

    // Calculate how much content is below the fold
    const contentBelow = scrollHeight - (scrollTop + containerHeight);

    if (contentBelow <= 0) {
      setDocumentsBelow(0);
      return;
    }

    // Estimate documents below based on average document height
    const estimatedDocumentHeight = 60;
    const estimatedDocumentsBelow = Math.ceil(contentBelow / estimatedDocumentHeight);

    setDocumentsBelow(Math.min(estimatedDocumentsBelow, documents.length));
  }, [documents, scrollContainerRef]);

  useEffect(() => {
    calculateDocumentsBelow();
  }, [documents, calculateDocumentsBelow]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      calculateDocumentsBelow();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [documents, calculateDocumentsBelow]);

  if (isEmpty(documents) && isEmpty(filesToSave)) {
    return (
      <Typography align="center" className={classes.noReferencedDocuments}>
        {NO_REFERENCED_DOCUMENTS}
      </Typography>
    );
  }

  return (
    <Stack direction="column">
      <Stack direction="column" sx={{ position: 'relative' }}>
        <Stack ref={scrollContainerRef} direction="column" spacing={1} sx={{ maxHeight: '15rem', overflow: 'auto' }}>
          {documents
            .sort((a, b) => {
              const hasMetadataA = documentSourceProps?.documentMetadata?.get(a.document_reference_id);
              const hasMetadataB = documentSourceProps?.documentMetadata?.get(b.document_reference_id);
              if (hasMetadataA && !hasMetadataB) {
                return -1;
              }
              if (!hasMetadataA && hasMetadataB) {
                return 1;
              }
              return 0;
            })
            .map((document: DocumentReferencesView) => {
              const metaData = documentSourceProps?.documentMetadata?.get(document.document_reference_id);
              const citationNumber = documentSourceProps?.documentCitations?.get(document.document_reference_id);
              return (
                <ReferencedDocument
                  key={`${document.file.id}-${selectedMeasurementDate.id}`}
                  document={document}
                  selectedMeasurementDateId={selectedMeasurementDate.id}
                  currentPage={currentPage}
                  isDisabled={isDisabled}
                  areDocumentsActionsDisabled={areDocumentsActionsDisabled}
                  onClick={docRef => onClick?.(document, docRef)}
                  citationMetadata={citationNumber as unknown as DocumentReference[]}
                  citationNumber={metaData ? [metaData] : undefined}
                />
              );
            })}
        </Stack>
        {documentsBelow > 0 && (
          <StyledChip label={`+${documentsBelow}`} size="small" className={classes.scrollIndicator} />
        )}
      </Stack>
      {!isEmpty(filesToSave) &&
        filesToSave?.map(({ document_reference_id, filename, file_type }: FileToSave) => (
          <PendingReference
            key={document_reference_id}
            id={+document_reference_id}
            filename={filename}
            fileType={file_type}
          />
        ))}
    </Stack>
  );
};

export default ReferencedDocumentsList;
